/**
 * Lista de pacientes del tenant. Buscador + tabla + crear nuevo paciente.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { TelefonoInput, formatearTelefono } from "../components/TelefonoInput";
import {
  ApoderadosForm,
  type LineaApoderado,
} from "../components/pacientes/ApoderadosForm";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, UserCheck, UserPlus, Users } from "lucide-react";
import {
  type GrupoSanguineo,
  type Paciente,
  type PacienteCreate,
  type TipoDocumento,
  createPaciente,
  listPacientes,
  reniecLookup,
} from "../api/pacientes";
import { useAuth } from "../auth/AuthProvider";
import { useBase } from "../auth/TenantBrandingProvider";
import { FECHA_MINIMA, edadLegible, hoyISO } from "../lib/edad";
import { ApiError } from "../api/client";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  Field,
  Input,
} from "../components/ui";

const TIPOS_DOC: { value: TipoDocumento; label: string }[] = [
  { value: "DNI", label: "DNI" },
  { value: "CE", label: "Carnet de extranjería" },
  { value: "PAS", label: "Pasaporte" },
  // Un recién nacido no tiene DNI durante sus primeras semanas: lo que trae
  // es el certificado de nacido vivo, y eso no puede impedir atenderlo.
  { value: "CNV", label: "Certificado de nacido vivo" },
];

/** Años cumplidos. Null si no se sabe la fecha. */
function edadDe(fecha: string | null | undefined): number | null {
  if (!fecha) return null;
  const n = new Date(fecha);
  if (Number.isNaN(n.getTime())) return null;
  const hoy = new Date();
  let e = hoy.getFullYear() - n.getFullYear();
  const m = hoy.getMonth() - n.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < n.getDate())) e -= 1;
  return e;
}

const GRUPOS_SANGRE: GrupoSanguineo[] = [
  "A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-",
];

export function PacientesListPage() {
  const { me } = useAuth();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [verTodos, setVerTodos] = useState(false);

  // El backend filtra automáticamente: si el usuario es médico (no admin/recepción),
  // solo ve sus pacientes asignados. `todos=true` desactiva ese filtro (walk-in).
  const roles = new Set((me?.roles ?? []).map((r) => r.codigo));
  const esMedicoEstricto =
    !me?.is_super_admin &&
    !roles.has("admin_tenant") &&
    !roles.has("recepcion") &&
    roles.has("medico");

  const { data, isLoading, error } = useQuery({
    queryKey: ["pacientes", search, verTodos],
    queryFn: () => listPacientes(search || undefined, 100, verTodos),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pacientes</h1>
          <p className="text-sm text-slate-500 mt-1">
            Registro de pacientes del policlínico. Búsqueda por DNI o nombre.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <UserPlus className="w-4 h-4" />
          Nuevo paciente
        </Button>
      </div>

      {esMedicoEstricto && (
        <div className="rounded-lg border border-[color:var(--color-link)] bg-[color-mix(in_srgb,var(--color-link)_8%,white)] px-4 py-2.5 text-xs text-[color:var(--color-link)] flex items-center justify-between">
          <span>
            {verTodos
              ? "Mostrando todos los pacientes del policlínico (modo walk-in)."
              : "Mostrando tus pacientes: los que atendiste, los que tienes citados y los que te derivaron."}
          </span>
          <button
            onClick={() => setVerTodos((v) => !v)}
            className="font-medium text-[color:var(--color-link)] hover:text-[color:var(--color-link)]"
          >
            {verTodos ? "Ver solo los míos" : "Buscar todos"}
          </button>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por DNI, nombre o apellido…"
              className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-slate-400"
            />
            {data && (
              <span className="text-xs text-slate-500">
                {data.length} {data.length === 1 ? "paciente" : "pacientes"}
              </span>
            )}
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading && (
            <div className="px-6 py-12 text-center text-sm text-slate-400">Cargando…</div>
          )}
          {error && (
            <div className="px-6 py-4">
              <Alert>No se pudo cargar la lista de pacientes.</Alert>
            </div>
          )}
          {data && data.length === 0 && !isLoading && (
            <div className="px-6 py-12 text-center">
              <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">
                {search
                  ? `Sin resultados para "${search}".`
                  : "Aún no hay pacientes registrados."}
              </p>
              {!search && (
                <button
                  onClick={() => setCreateOpen(true)}
                  className="mt-3 text-sm text-[color:var(--color-link)] hover:text-[color:var(--color-link)] font-medium"
                >
                  Registrar el primero
                </button>
              )}
            </div>
          )}
          {data && data.length > 0 && <PacientesTable rows={data} />}
        </CardBody>
      </Card>

      <CreatePacienteDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

function PacientesTable({ rows }: { rows: Paciente[] }) {
  const base = useBase();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-y border-slate-200">
          <tr>
            <Th>Documento</Th>
            <Th>Nombre completo</Th>
            <Th>Edad</Th>
            <Th>Sexo</Th>
            <Th>Contacto</Th>
            <Th>Estado</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p: any) => (
            <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/50">
              <td className="px-6 py-3">
                <div className="font-mono text-xs text-slate-700">
                  {p.num_documento ? (
                    `${p.tipo_documento} ${p.num_documento}`
                  ) : (
                    <Badge variant="warning">Documento por regularizar</Badge>
                  )}
                </div>
              </td>
              <td className="px-6 py-3">
                <Link
                  to={`${base}/pacientes/${p.id}`}
                  className="text-[color:var(--color-link)] hover:text-[color:var(--color-link)] font-medium"
                >
                  {p.nombre_completo}
                </Link>
              </td>
              <td className="px-6 py-3 text-slate-600">
                {edadEnAnios(p.fecha_nacimiento)}
              </td>
              <td className="px-6 py-3 text-slate-600">
                {p.sexo === "M" ? "M" : p.sexo === "F" ? "F" : "—"}
              </td>
              <td className="px-6 py-3 text-xs">
                {p.telefono && (
                  <div className="text-slate-700">{formatearTelefono(p.telefono)}</div>
                )}
                {p.email && <div className="text-slate-400">{p.email}</div>}
                {!p.telefono && !p.email && <span className="text-slate-300">—</span>}
              </td>
              <td className="px-6 py-3">
                <Badge variant={p.activo ? "success" : "danger"}>
                  {p.activo ? "Activo" : "Inactivo"}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
      {children}
    </th>
  );
}

function edadEnAnios(fecha: string | null): string {
  if (!fecha) return "—";
  const nacimiento = new Date(fecha);
  const hoy = new Date();
  let anios = hoy.getFullYear() - nacimiento.getFullYear();
  const m = hoy.getMonth() - nacimiento.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) {
    anios--;
  }
  return anios >= 0 ? `${anios} años` : "—";
}

// ---------- Create dialog ----------

/**
 * Alta rápida de paciente. Se usa desde la lista de pacientes y desde Citas,
 * donde recepción registra al que llega sin ficha y sigue de largo a agendarle.
 */
export function CreatePacienteDialog({
  open,
  onClose,
  documentoInicial,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /** Lo que recepción ya tecleó en el buscador: no se escribe dos veces. */
  documentoInicial?: string;
  /** Recibe el paciente creado, para encadenar con la cita sin volver a buscarlo. */
  onCreated?: (p: Paciente) => void;
}) {
  const queryClient = useQueryClient();
  const emptyForm: PacienteCreate = useMemo(
    () => ({
      tipo_documento: "DNI",
      num_documento: "",
      nombres: "",
      ap_paterno: "",
      ap_materno: "",
      fecha_nacimiento: null,
      sexo: null,
      email: "",
      telefono: "",
      direccion: "",
      grupo_sanguineo: null,
      consentimiento_medio: null,
    }),
    [],
  );
  const [form, setForm] = useState<PacienteCreate>(emptyForm);
  const [apoderados, setApoderados] = useState<LineaApoderado[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reniecLoading, setReniecLoading] = useState(false);
  const [reniecMessage, setReniecMessage] = useState<string | null>(null);

  // Arrancamos con lo tecleado en la búsqueda si parece un documento.
  useEffect(() => {
    if (open) {
      setForm({ ...emptyForm, num_documento: documentoInicial ?? "" });
      setApoderados([]);
      setError(null);
      setReniecMessage(null);
    }
  }, [open, documentoInicial, emptyForm]);

  // La edad manda sobre lo que se pide: es lo único que distingue a un bebé
  // de un adulto en este formulario.
  const edad = edadDe(form.fecha_nacimiento);
  const esMenor = edad != null && edad < 18;

  const mutation = useMutation({
    mutationFn: (payload: PacienteCreate) => createPaciente(payload),
    onSuccess: (creado) => {
      queryClient.invalidateQueries({ queryKey: ["pacientes"] });
      onClose();
      setForm(emptyForm);
      setError(null);
      setReniecMessage(null);
      onCreated?.(creado);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        const detail = (err.payload as { detail?: string })?.detail;
        setError(detail ?? "Ya existe un paciente con ese documento.");
      } else if (err instanceof ApiError && err.status === 400) {
        const detail = (err.payload as { detail?: string })?.detail;
        setError(detail ?? "Datos inválidos.");
      } else if (err instanceof ApiError && err.status === 422) {
        const payload = err.payload as { detail?: unknown } | null;
        const detail = payload?.detail;
        if (Array.isArray(detail) && detail.length > 0) {
          const first = detail[0] as { loc?: unknown[]; msg?: string };
          const field = Array.isArray(first.loc)
            ? String(first.loc[first.loc.length - 1] ?? "campo")
            : "campo";
          setError(`Error en "${field}": ${first.msg ?? "valor inválido"}`);
        } else {
          setError("Datos inválidos. Revisa los campos.");
        }
      } else {
        setError("Error al crear. Intenta de nuevo.");
      }
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    // Limpia strings vacíos a undefined antes de enviar
    if (esMenor && apoderados.length === 0) {
      setError(
        `El paciente tiene ${edad} años: registra a su madre, padre o apoderado.`,
      );
      return;
    }
    mutation.mutate({
      ...form,
      // Vacío significa "pendiente de regularizar", no "una cadena vacía":
      // mandarlo como "" hacía fallar la validación de longitud mínima.
      num_documento: form.num_documento?.trim() || undefined,
      ap_materno: form.ap_materno || undefined,
      email: form.email || undefined,
      telefono: form.telefono || undefined,
      direccion: form.direccion || undefined,
      // Los campos que empiezan con "_" son de la interfaz y no viajan.
      apoderados: apoderados.map(({ _nombre, _yaExistia, _aCargoDe, ...a }) => ({
        ...a,
        telefono: a.telefono || undefined,
        email: a.email || undefined,
      })),
    });
  }

  async function handleReniec() {
    if (form.tipo_documento !== "DNI" || (form.num_documento ?? "").length !== 8) {
      setReniecMessage("Necesitas un DNI de 8 dígitos.");
      return;
    }
    setReniecLoading(true);
    setReniecMessage(null);
    try {
      const r = await reniecLookup(form.num_documento ?? "");
      setForm({
        ...form,
        nombres: r.nombres,
        ap_paterno: r.ap_paterno,
        ap_materno: r.ap_materno,
        sexo: r.sexo,
        fecha_nacimiento: r.fecha_nacimiento,
      });
      setReniecMessage(
        r.fuente === "MOCK"
          ? "Datos de prueba (RENIEC mock en dev — en producción será data real)."
          : "Datos consultados a RENIEC.",
      );
    } catch {
      setReniecMessage("No se pudo consultar RENIEC.");
    } finally {
      setReniecLoading(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Nuevo paciente"
      description="Lo mínimo para poder atenderlo. El resto se completa después en su ficha."
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}

        {/* Documento */}
        <div className="grid grid-cols-[140px_1fr_auto] gap-3">
          <Field label="Tipo doc." htmlFor="tipo_doc">
            <select
              id="tipo_doc"
              value={form.tipo_documento}
              onChange={(e) =>
                setForm({ ...form, tipo_documento: e.target.value as TipoDocumento })
              }
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              {TIPOS_DOC.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Número"
            htmlFor="num_doc"
            hint={
              esMenor
                ? "Si aún no tiene DNI, déjalo vacío y regularízalo después."
                : undefined
            }
          >
            <Input
              id="num_doc"
              value={form.num_documento ?? ""}
              // El DNI son ocho dígitos: dejar escribir letras solo sirve para
              // que el error aparezca al guardar, con el paciente esperando.
              onChange={(e) =>
                setForm({
                  ...form,
                  num_documento:
                    form.tipo_documento === "DNI"
                      ? e.target.value.replace(/\D/g, "").slice(0, 8)
                      : e.target.value.toUpperCase(),
                })
              }
              maxLength={form.tipo_documento === "DNI" ? 8 : 20}
              inputMode={form.tipo_documento === "DNI" ? "numeric" : "text"}
              // En un menor puede quedar pendiente: a un recién nacido lo
              // traen antes de tener DNI, y un número inventado en la ficha de
              // un paciente es peor que un campo vacío.
              required={!esMenor}
              placeholder={esMenor ? "Puede quedar pendiente" : undefined}
              className="font-mono"
            />
          </Field>
          <div className="flex items-end">
            <button
              type="button"
              onClick={handleReniec}
              disabled={reniecLoading || form.tipo_documento !== "DNI"}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Autocompletar desde RENIEC"
            >
              <UserCheck className="w-3.5 h-3.5" />
              {reniecLoading ? "Consultando…" : "RENIEC"}
            </button>
          </div>
        </div>
        {reniecMessage && (
          <p className="text-xs text-slate-500 -mt-2">{reniecMessage}</p>
        )}

        {/* Nombres */}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Nombres" htmlFor="nombres">
            <Input
              id="nombres"
              value={form.nombres}
              onChange={(e) => setForm({ ...form, nombres: e.target.value })}
              maxLength={200}
              required
            />
          </Field>
          <Field label="Apellido paterno" htmlFor="ap_paterno">
            <Input
              id="ap_paterno"
              value={form.ap_paterno}
              onChange={(e) => setForm({ ...form, ap_paterno: e.target.value })}
              maxLength={100}
              required
            />
          </Field>
          <Field label="Apellido materno" htmlFor="ap_materno">
            <Input
              id="ap_materno"
              value={form.ap_materno ?? ""}
              onChange={(e) => setForm({ ...form, ap_materno: e.target.value })}
              maxLength={100}
            />
          </Field>
        </div>

        {/* Demográficos */}
        <div className="grid grid-cols-3 gap-3">
          <Field
            label="Fecha de nacimiento"
            htmlFor="fnac"
            // La edad calculada al vuelo es la mejor verificación que hay: un
            // dedazo en el año salta a la vista al leer "58 años" en un bebé.
            hint={edadLegible(form.fecha_nacimiento) ?? undefined}
          >
            <Input
              id="fnac"
              type="date"
              value={form.fecha_nacimiento ?? ""}
              // Nadie nace mañana. Sin el tope, una fecha futura daba edad
              // negativa y el formulario la aceptaba sin decir nada.
              max={hoyISO()}
              min={FECHA_MINIMA}
              onChange={(e) =>
                setForm({ ...form, fecha_nacimiento: e.target.value || null })
              }
            />
          </Field>
          <Field label="Sexo" htmlFor="sexo">
            <select
              id="sexo"
              value={form.sexo ?? ""}
              onChange={(e) =>
                setForm({ ...form, sexo: (e.target.value || null) as any })
              }
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">—</option>
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
              <option value="O">Otro</option>
            </select>
          </Field>
          <Field label="Grupo sanguíneo" htmlFor="gs">
            <select
              id="gs"
              value={form.grupo_sanguineo ?? ""}
              onChange={(e) =>
                setForm({ ...form, grupo_sanguineo: (e.target.value || null) as any })
              }
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">—</option>
              {GRUPOS_SANGRE.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {/* Contacto.
            Con un menor el formulario cambia solo: un bebé no tiene teléfono
            ni correo, y pedírselos es pedir un dato que no existe. El contacto
            pasa a ser el de su apoderado, que es a quien de verdad se llama. */}
        {esMenor ? (
          <ApoderadosForm valor={apoderados} onChange={setApoderados} obligatorio />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Teléfono" htmlFor="tel">
                <TelefonoInput
                  id="tel"
                  value={form.telefono}
                  onChange={(v) => setForm({ ...form, telefono: v })}
                />
              </Field>
              <Field label="Email" htmlFor="email">
                <Input
                  id="email"
                  type="email"
                  value={form.email ?? ""}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  maxLength={255}
                  placeholder="paciente@correo.pe"
                />
              </Field>
            </div>
            {form.fecha_nacimiento && (
              <details className="text-xs text-slate-500">
                <summary className="cursor-pointer">
                  ¿Necesita un apoderado o contacto de emergencia?
                </summary>
                <div className="mt-2">
                  <ApoderadosForm
                    valor={apoderados}
                    onChange={setApoderados}
                    obligatorio={false}
                  />
                </div>
              </details>
            )}
          </>
        )}

        <Field label="Dirección" htmlFor="direccion" hint="Opcional.">
          <Input
            id="direccion"
            value={form.direccion ?? ""}
            onChange={(e) => setForm({ ...form, direccion: e.target.value })}
            maxLength={500}
          />
        </Field>

        {/* Consentimiento Ley 29733, en una línea.
            Tres tarjetas para esto era ruido en un formulario que se rellena
            con el paciente esperando. Y por defecto va "Pendiente" porque el
            destino es que lo acepte él mismo desde su app: recepción no debería
            estar consintiendo en nombre de nadie. */}
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <span className="text-sm text-slate-700">Consentimiento de datos</span>
          <select
            value={form.consentimiento_medio ?? ""}
            onChange={(e) =>
              setForm({
                ...form,
                consentimiento_medio: (e.target.value || null) as
                  | "FISICO"
                  | "VERBAL"
                  | "DIGITAL"
                  | null,
              })
            }
            aria-label="Cómo otorgó el paciente su consentimiento"
            className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm"
          >
            <option value="">Pendiente — lo aceptará el paciente</option>
            <option value="FISICO">Firmó el formato</option>
            <option value="VERBAL">Lo dio verbalmente</option>
          </select>
          <span className="text-xs text-slate-500">
            Ley N° 29733 · queda con tu nombre y la fecha
          </span>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            loading={mutation.isPending}
          >
            Crear paciente
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
